/* eslint-disable import/no-cycle */
import { Dispatch } from 'react';
import { RegistererState, UserAgentDelegate, Core, Notification } from 'sip.js';
import * as Sentry from '@sentry/browser';
import { RootState } from '~/store/config';
import Phone, { IPhoneOptions } from '~/lib/Phone';
import { EnumCallStatus, IAnnouncements } from '~/store/reducers/phone';
import { SERVER_URL, DEV_SERVER_URL } from '~/config/config';
import { connectSocket } from '~/services/socket';
import api from '~/services';
import { log, electron, getToken } from '~/helpers';
import * as actionTypes from './actionTypes';
import * as actions from './index';

interface IVoiceMailMessages {
  unheard: string;
  heard: string;
}

interface IFetchAnnouncements {
  type: string;
  announcements: IAnnouncements[];
}

export const registerSession = () => () => {};

export const unregisterUA = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  try {
    const { ua } = getState().phone;

    if (ua) {
      await ua.unregister();
    }
  } catch (err) {
    log.error(`Failure to unregister the extension. Reason: ${err}`);
  }
};

export const registerUA = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  try {
    const { ua } = getState().phone;

    if (ua) {
      await ua.register();
    }
  } catch (err) {
    log.error(`Failure to register the extension. Reason: ${err}`);
  }
};

export const unRegisterSession = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  try {
    const { ua } = getState().phone;

    if (ua) {
      await ua.stop();

      dispatch({
        type: actionTypes.PHONE_SET_NEW_UA,
        ua: null,
      });
    }
  } catch (err) {
    log.error('Failure to dispose of the current SIP session. Reason: ', err);
  }
};

export const phoneSetNewUA = (uac: Phone) => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { ua } = getState().phone;

  // Stop any previous instance of the Phone to avoid any duplicate WebSocket connection, etc.
  if (ua) {
    ua.stop();
  }

  dispatch({
    type: actionTypes.PHONE_SET_NEW_UA,
    ua: uac,
  });
};

export const callInProgress = (isCallInProgress: boolean) => ({
  type: actionTypes.CALL_IN_PROGRESS,
  callInProgress: isCallInProgress,
});

export const voiceMessageReceived = (messages: IVoiceMailMessages) => ({
  type: actionTypes.VOICE_MESSAGE_RECEIVED,
  voiceMessages: messages,
});

export const callStatus = (status: EnumCallStatus) => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  try {
    // @ts-ignore
    const { historyFilterOptions } = getState();
    const { selectedTab } = historyFilterOptions;

    dispatch({
      type: actionTypes.CALL_STATUS,
      callStatus: status,
    });

    if (status === EnumCallStatus.CALL_FINISHED) {
      const userInfo = await api.users.get.userInfo();

      if (userInfo && userInfo.data) {
        const { user_extension } = userInfo.data;

        localStorage.setItem('userExtension', user_extension);
        dispatch(actions.authSetUserExtension(user_extension));

        // Update calls logs after 10 seconds the calls finished
        setTimeout(() => {
          const tabSelected = selectedTab?.findIndex(
            (e: string) => e === 'My Calls'
          );
          dispatch(actions.resetHistoryFilterOptions());
          dispatch(
            actions.fetchCalllogs(tabSelected === -1 ? null : user_extension)
          );
        }, 10000);
      }
    }
  } catch (err) {
    log.error('Failed to change call status.', err);
  }
};

export const startUA = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  try {
    const sipCredentials = await api.accounts.get.extensionInfo();
    const { hs_user, hs_pass, ring_timeout } = sipCredentials.data;
    const { dev } = getState().system;

    const auth = {
      user: hs_user,
      pass: hs_pass,
      userName: sipCredentials.data.name,
      extension: sipCredentials.data.number,
    };

    dispatch({
      type: actionTypes.UPDATE_CREDENTIALS,
      sipCredentials: auth,
    });

    const userAgentDelegate: UserAgentDelegate = {
      onConnect: () => {
        log.warn('UA Transport is connected, trying to send the SIP REGISTER.');
        const { ua } = getState().phone;
        const { isBusy } = getState().system;

        if (!isBusy) {
          ua.handleRegistration();
        } else {
          dispatch(actions.statusSystem(actionTypes.STATUS.BUSY));
          electron.changePhoneStatus('Busy', false);
        }
      },
      onDisconnect: async error => {
        log.warn('UA Transport is disconnected.', error);
        const { ua } = getState().phone;
        // const { data } = getState().callCurrent;

        // If the Transport is closed then terminate every active call session.
        // if (data.length > 0) {
        //   data.forEach(call => {
        //     call.session.bye();
        //   });
        // }

        // If Transport is down always set to Unavailable.
        dispatch(actions.statusSystem(actionTypes.STATUS.UNAVAILABLE));
        electron.changePhoneStatus('Unavailable', false);

        // On disconnect, cleanup invalid registrations
        // ua.registerer.unregister().catch((err: any) => {
        //   log.info(
        //     `NUACOM_LOG: UA Unregistration has failed, must probably because a SIP REGISTER is already in progress. Reason: ${err}`
        //   );
        // });
        if (ua !== null) {
          ua.attemptReconnection();
        }
      },
      onInvite: async invitation => {
        const { data } = getState().callCurrent;
        const { status } = getState().system;

        // If there are 2 calls in progress or status is Busy reject this invitation.
        if (data.length > 1 || status === actionTypes.STATUS.BUSY) {
          await invitation.reject();

          return;
        }

        // Start playing the ringtone.
        dispatch(actions.PlayRing('ringTone', true));
        dispatch(actions.addNewSession(invitation, { invite: false }));
      },
      onNotify: (notification: Notification) => {
        // Send the 200 OK to the server.
        notification.accept();

        const notifyBody = notification.request.body.split('\r\n');

        for (let i = 0; i < notifyBody.length; i += 1) {
          if (notifyBody[i].includes('Voice-Message')) {
            /* ["Voice-Message:", "219/2", "(0/0)"] -->> ["219/2"].split('/) -->> [219, 2] */
            const amount = notifyBody[i].split(' ')[1].split('/');
            const voiceMessages = {
              unheard: amount[0],
              heard: amount[1],
            };
            dispatch(voiceMessageReceived(voiceMessages));
          }
        }
      },
    };

    const registererState = (newState: RegistererState) => {
      const { isBusy } = getState().system;
      const { session, isVideoCallActive } = getState().callCurrent;

      switch (newState) {
        case RegistererState.Registered:
          log.info('RegistererState Registered');
          if (isBusy && !session) {
            electron.changePhoneStatus('Busy', false);
          } else if (!session) {
            dispatch(actions.statusSystem(actionTypes.STATUS.AVAILABLE));
            electron.changePhoneStatus('Available', false);
          }
          break;

        case RegistererState.Unregistered:
          log.info(`RegistererState: Unregistered`);
          if (isBusy && !session) {
            electron.changePhoneStatus('Busy', false);
          } else if (!session && !isVideoCallActive) {
            dispatch(actions.statusSystem(actionTypes.STATUS.UNAVAILABLE));
            electron.changePhoneStatus('Unavailable', false);
          }
          break;

        default:
      }
    };

    const requestDelegate: Core.OutgoingRequestDelegate = {
      onReject: response => {
        const pRegistrarError = response.message.getHeader('P-Registrar-Error');

        switch (pRegistrarError) {
          case 'Too many registered contacts':
            dispatch({
              type: actionTypes.PHONE_REGISTER_ERROR,
              error:
                'You have exceeded the maximum number of 3 connected devices with the same extension.',
            });
            log.error('Too many registered contacts');
            break;
          default:
            dispatch({
              type: actionTypes.PHONE_REGISTER_ERROR,
              error: 'You are not register.',
            });
            log.error('registerDelegate UA is Unregistered');
        }
      },
    };

    const outgoingRequestDelegate: Core.OutgoingRequestDelegate = {
      onTrying: () => {
        log.info('OutgoingRequestDelegate onTrying');
        dispatch(actions.PlayRing('outgoingRing', true));
      },
      onAccept: (incomingResponse: Core.AckableIncomingResponseWithSession) => {
        log.info('outgoingRequestDelegate: onAccept');
        const { ack, session } = incomingResponse;
        ack(); // Send 200 ACK back to the sip server

        dispatch(actions.PlayRing('outgoingRing', false));
        // @ts-ignore dialogState in fact exists inside session
        dispatch(actions.updateCall('accepted', session.dialogState.callId));
      },
      onReject: response => {
        log.info('outgoingRequestDelegate: onReject');
        if (
          response.message.statusCode === 486 ||
          response.message.statusCode === 408
        ) {
          dispatch(actions.PlayRing('busyTone', true));
        }
      },
    };

    const options: IPhoneOptions = {
      userAgentOptions: {
        sipServer: dev ? DEV_SERVER_URL : SERVER_URL,
        username: hs_user,
        password: hs_pass,
        ringTimeout: ring_timeout,
        delegate: userAgentDelegate,
        outgoingRequestDelegate,
      },
      registererOptions: {
        registererState,
        registerOptions: {
          requestDelegate,
        },
      },
    };

    const phone = new Phone(options);

    await dispatch(phoneSetNewUA(phone));

    phone.start();
  } catch (err) {
    log.error('Failed to execute startUA.', err);
  }
};

export const changeCredentials = () => async (dispatch: Dispatch<any>) => {
  try {
    const userInfo = await api.users.get.userInfo();
    const extInfo = await api.accounts.get.extensionInfo();
    const { data } = userInfo;
    if (data && extInfo.data) {
      const userExtension = data.user_extension;
      const sipAccount = extInfo.data.hs_user;
      const sipPass = extInfo.data.hs_pass;

      const auth = {
        user: sipAccount,
        pass: sipPass,
        userName: data.name,
        extension: userExtension,
      };

      dispatch({
        type: actionTypes.UPDATE_CREDENTIALS,
        sipCredentials: auth,
      });
    }
  } catch (err) {
    log.error('Failed to change credentials.', err);
  }
};

// NOTE: The actions below is related to the WebSocket for the Team extension status.
export const updateExtensions = (data: { agent: string; status: string }) => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { extensions } = getState().phone;
  const { agent, status } = data;
  const index = extensions.findIndex((e: any) => String(e.number) === agent);
  const values = extensions.filter((e: any) => String(e.number) !== agent);
  if (index !== -1) {
    dispatch({
      type: actionTypes.UPDATE_EXTENSIONS,
      extensions: [
        ...values,
        {
          number: extensions[index].number,
          name: extensions[index].name,
          status,
          subscription: extensions[index].subscription,
        },
      ],
    });
  }
};

export const logoutUserAndCreateSentryReport = () => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { data: activeCalls } = getState().callCurrent;
  const { account_id, user_extension, revokeRequested } = getState().auth;

  if (!revokeRequested) {
    dispatch({
      type: actionTypes.SET_SESSION_REVOKE_REQUESTED,
    });

    const createSentryReportAndRedirectToSignIn = () => {
      Sentry.captureMessage(
        `NUACOM_LOG: ${account_id}-${user_extension} Destkop Softphone receive session revoke from the backend via websocket.`,
        Sentry.Severity.Info
      );

      window.location.href = '/logout';
    };

    if (!activeCalls?.length) {
      createSentryReportAndRedirectToSignIn();
    } else {
      const interval = setInterval(() => {
        log.info('Waiting call finish to logout the current user.');

        const activeFinishedLen = activeCalls?.filter(
          e => e.status === 'CALL_FINISHED'
        )?.length;

        if (
          !activeCalls?.length ||
          (activeFinishedLen && activeCalls.length === activeFinishedLen)
        ) {
          clearInterval(interval);
          createSentryReportAndRedirectToSignIn();
        }
      }, 1000);
    }
  }
};

export const connectWebSocket = (isNewConnection: boolean) => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { socketIo } = getState().phone;
  const token = getToken();

  try {
    if (!socketIo && token && isNewConnection) {
      const socket = connectSocket(token);

      socket.connect();

      socket.on('connect', () => {
        log.info('socket.io connected!');
      });

      socket.on('agent_status', (data: { agent: string; status: string }) => {
        dispatch(updateExtensions(data));
      });

      // Destkop Softphone receive session revoke from the backend.
      socket.on('log_out', () => {
        log.warn(
          'Destkop Softphone receive session revoke from the backend via websocket.'
        );

        dispatch(logoutUserAndCreateSentryReport());
      });

      socket.on('after_call_work_rules_changed', () => {
        dispatch(actions.fetchAfterCallRules());
      });

      // Admin's from the user portal can set the currently registered extension to Busy.
      socket.on('status_changed', async () => {
        const { ua } = getState().phone;

        dispatch(actions.setIsBusy(true));
        dispatch(actions.statusSystem(actionTypes.STATUS.BUSY));
        ua?.unregister();
      });

      dispatch({
        type: actionTypes.SAVE_SOCKET_IO,
        socketIo: socket,
      });
    } else if (socketIo && !socketIo.connected && token) {
      socketIo.connect();
    }
  } catch (err) {
    log.error('Failed to handle websocket connect stage.', err);
  }
};

export const disconnectWebSocket = () => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { socketIo } = getState().phone;

  if (socketIo) {
    socketIo.close();
    socketIo.disconnect();
    log.warn('socket.io disconnected!');
  }
  dispatch({
    type: actionTypes.SAVE_SOCKET_IO,
    socketIo: null,
  });
};

export const refreshWebSocket = () => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { socketIo } = getState().phone;

  if (socketIo) {
    socketIo.emit('refresh');
  }
};

export const saveFavoriteAgents = (favoriteAgents: []) => (
  dispatch: Dispatch<any>
) => {
  dispatch({
    type: actionTypes.SAVE_FAVORITE_AGENTS,
    favoriteAgents,
  });
};

export const fetchFavoriteAgents = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { favoriteAgents: favoriteAgentsReducer } = getState().phone;

  try {
    const response = await api.users.get.favoriteAgents();
    const agents = response?.data?.agents;

    if (agents?.length > 0) {
      let favoriteAgentsResponse = [];

      if (agents.includes(',')) {
        favoriteAgentsResponse = agents.split(',');
      } else {
        favoriteAgentsResponse.push(agents);
      }

      if (
        JSON.stringify(favoriteAgentsReducer) !==
        JSON.stringify(favoriteAgentsResponse)
      ) {
        dispatch(saveFavoriteAgents(favoriteAgentsResponse));
      }
    } else {
      dispatch(saveFavoriteAgents([]));
    }
  } catch (err) {
    log.error('Failed to fetch favorite agents.', err);
  }
};

export const fetchExtensions = () => async (dispatch: Dispatch<any>) => {
  try {
    const extensionsData = await api.accounts.get.allExtensions();
    const extensionWithStatus = extensionsData.data.reduce(
      (prev: any, next: any) => prev.concat({ ...next, status: '4' }),
      []
    );

    dispatch({
      type: actionTypes.GET_EXTENSIONS,
      extensions: extensionWithStatus,
    });
  } catch (err) {
    log.error('Failed to fetch extensions with status.', err);
  }
};

export const fetchAnnouncements = () => async (
  dispatch: Dispatch<IFetchAnnouncements>
) => {
  try {
    const announcs = await api.announcements.get.all();

    if (announcs.data) {
      const newAnnoun: IAnnouncements[] = Object.keys(announcs.data)
        .filter(e => announcs.data[e].duration > 0)
        .map(e => {
          return {
            id: announcs.data[e].id,
            name: announcs.data[e].name,
            duration: announcs.data[e].duration,
          };
        });

      dispatch({
        type: actionTypes.SET_ANNOUNCEMENTS,
        announcements: newAnnoun,
      });
    }
  } catch (err) {
    log.error('Failure to retrieve the announcements. Reason: ', err);
  }
};

export const resetPhones = () => (dispatch: Dispatch<any>) => {
  dispatch({
    type: actionTypes.RESET_PHONES,
  });
};
